/**
 * Builds props for the ERP messenger MFE (`ENV_MESSENGER_URL`).
 *
 * **Contract:** `erpMessengerLayoutMode` lives on `IMessengerAppCommonParams` in `@xometry/ui` (optional). Older
 * messenger bundles that ignore it behave as classic layout (`!== 'compact'` in the MFE). `onRequestClassicLayout`
 * is an extra optional callback used by the current messenger-front only; it is omitted unless the compact layout
 * passes a handler — no strict-schema runtime on the wire beyond plain object props.
 */
import {
  EMessengerAppName,
  type MessengerEventBus,
  type TMessengerAppParams,
  type TMessengerErpLayoutMode,
} from '@xometry/ui';
import { toJS } from 'mobx';
import type { TSideOverlayContext } from 'stores/SideOverlay/SideOverlayStore';
import { ESideOverlayType } from 'stores/SideOverlay/SideOverlayStore';
import { getMessengerAppConfig } from 'utils/getMessengerAppConfig';

type AmplitudeLoggerFn = (args: { event: string; attrs?: Record<string, string> }) => void;

export type TErpMainMessengerHostAppParams = TMessengerAppParams<EMessengerAppName.ERP_MAIN> & {
  onRequestClassicLayout?: () => void;
};

type MessengerOverlayContext = Extract<TSideOverlayContext, { type: ESideOverlayType.Messenger }>;

interface BuildPropsArgs {
  context: MessengerOverlayContext;
  setEventBusRef: (eventBus: MessengerEventBus) => MessengerEventBus;
  amplitudeLogger: AmplitudeLoggerFn;
  isMobile: boolean;
  erpMessengerLayoutMode: TMessengerErpLayoutMode;
  onRequestClassicLayout?: () => void;
}

export const buildErpMainMessengerMicroFrontendProps = (args: BuildPropsArgs): TErpMainMessengerHostAppParams => {
  const messengerAppConfig = getMessengerAppConfig();

  const { context, setEventBusRef, amplitudeLogger, isMobile, erpMessengerLayoutMode, onRequestClassicLayout } = args;

  const common = {
    appName: EMessengerAppName.ERP_MAIN as const,
    config: messengerAppConfig,
    setEventBusRef,
    amplitudeLogger,
    isMobile,
    launchContext: context.launchContext,
    erpMessengerLayoutMode,
    ...(onRequestClassicLayout ? { onRequestClassicLayout } : {}),
  };

  if (context.initialScreen) {
    return {
      ...common,
      initialScreen: toJS(context.initialScreen),
    };
  }

  return {
    ...common,
    initialMessageId: context.initialMessageId,
  };
};
